import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { QueryParams, Routine, SuggestionStatus, YesOrNo } from '../types';
import { getRoutineByTemplateId } from '../utils/httpRequest';
import { RootState } from '../store';

export interface RoutineState {
  routines: Routine[];
  loading: boolean;
  error?: string;
}

const initialState: RoutineState = {
  routines: [],
  loading: false,
};

export const fetchRoutines = createAsyncThunk(
  'machines/fetchRoutines',
  async ({
    templateID,
    params,
  }: {
    templateID: string;
    params?: QueryParams;
  }) => {
    const routines = await getRoutineByTemplateId(templateID, params);

    return routines;
  }
);

export const routinesSlice = createSlice({
  name: 'routine',
  initialState,
  reducers: {
    setRoutines: (state, action: PayloadAction<Routine[]>) => {
      state.routines = action.payload;
    },
    addRoutines: (state, action: PayloadAction<Routine>) => {
      state.routines = [action.payload, ...state.routines];
    },
    removeRoutine: (state, action: PayloadAction<string>) => {
      state.routines = state.routines?.filter(
        ({ routineID }) => routineID !== action.payload
      );
    },
    updateRoutine: (state, action: PayloadAction<Routine>) => {
      const index = state.routines.findIndex(
        ({ routineID }) => routineID === action.payload.routineID
      );
      const nextRoutines = [...state.routines];
      nextRoutines[index] = action.payload;
      state.routines = [...nextRoutines];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoutines.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRoutines.fulfilled, (state, action) => {
        state.routines = action.payload.data || [];
        state.loading = false;
      })
      .addCase(fetchRoutines.rejected, (state, action) => {
        state.error = 'Error';
        state.loading = false;
      });
  },
});

export const { setRoutines, addRoutines, removeRoutine, updateRoutine } =
  routinesSlice.actions;

export default routinesSlice.reducer;

export const selectRoutines = (state: RootState) =>
  state.routines.routines || [];

export const selectRoutinesForDiagnose = (state: RootState) =>
  (state.routines.routines || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.NO ||
      i.suggestionStatus === SuggestionStatus.REVIEWED
  );

export const selectIsFetchingRoutines = (state: RootState) =>
  state.routines.loading;

export const selectCurrentRoutines = (state: RootState) =>
  (state.routines.routines || []).filter(
    (i) =>
      i.isSuggestion === YesOrNo.NO ||
      i.suggestionStatus === SuggestionStatus.REVIEWED ||
      i.suggestionStatus === SuggestionStatus.APPROVED
  );
